import { captureScan, type ScanProgress } from '../audio/scanCapture.ts';
import { captureBlocker } from '../audio/capture.ts';
import { describeDuration, scanTimeline, TILT_TARGET_DEG } from '../dsp/pacing.ts';
import { requestOrientationPermission } from '../sensors/orientation.ts';
import { createMapView, type MapViewHandle } from './mapView.ts';
import type { Reconstruction } from '../dsp/reconstruct.ts';
import type { WorkerRequest, WorkerResponse } from '../workers/reconstruct.worker.ts';
import ReconstructWorker from '../workers/reconstruct.worker.ts?worker';
import type { SceneName } from '../sim/scene3d.ts';

/**
 * Everything the mapping mode needs from the page, and nothing else.
 *
 * Kept behind an interface so the scan can take over the stage from the
 * single-measurement view without either of them knowing about the other:
 * they draw different claims and share only a canvas.
 */
export interface ScanContext {
  setStatus(message: string, kind?: 'idle' | 'busy' | 'error'): void;
  setWarnings(warnings: string[]): void;
  isBusy(): boolean;
  setBusy(busy: boolean): void;
  temperatureC(): number;
  demoScene(): string;
  /** Tear down the sphere view so the map can have the canvas. */
  releaseStage(): void;
  hasGpu(): boolean;
  /** Hand the data section over, so the two modes never both write to it. */
  claimDataSection(): void;
}

function need<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element as T;
}

const SCENES: SceneName[] = ['bedroom', 'office', 'kitchen'];

/** Compass bearing of a horizontal normal, degrees clockwise from -Z. */
function bearingOf(normal: readonly number[]): number {
  return ((Math.atan2(normal[0], -normal[2]) * 180) / Math.PI + 360) % 360;
}

function cell(row: HTMLTableRowElement, text: string, className?: string): void {
  const td = row.insertCell();
  td.textContent = text;
  if (className) td.className = className;
}

function head(table: HTMLTableElement, labels: string[]): void {
  const row = table.createTHead().insertRow();
  for (const label of labels) {
    const th = document.createElement('th');
    th.textContent = label;
    row.append(th);
  }
}

function emptyRow(table: HTMLTableElement, span: number, text: string): void {
  const row = table.insertRow();
  const td = row.insertCell();
  td.className = 'empty';
  td.colSpan = span;
  td.textContent = text;
}

export function initScanMode(context: ScanContext): void {
  const ui = {
    scan: need<HTMLButtonElement>('scan'),
    stop: need<HTMLButtonElement>('stopScan'),
    measure: need<HTMLButtonElement>('measure'),
    demo: need<HTMLButtonElement>('demo'),
    scanLength: need<HTMLSelectElement>('scanLength'),
    cueMode: need<HTMLSelectElement>('cueMode'),
    scanNote: need<HTMLElement>('scanNote'),
    progress: need<HTMLElement>('scanProgress'),
    ready: need<HTMLElement>('scanReady'),
    count: need<HTMLElement>('scanCount'),
    bar: need<HTMLElement>('scanBar'),
    turn: need<HTMLElement>('scanTurn'),
    pace: need<HTMLElement>('scanPace'),
    meterTurned: need<HTMLElement>('meterTurned'),
    meterCoverage: need<HTMLElement>('meterCoverage'),
    meterPitch: need<HTMLElement>('meterPitch'),
    meterLeft: need<HTMLElement>('meterLeft'),
    canvas: need<HTMLCanvasElement>('roomCanvas'),
    overlay: need<HTMLElement>('roomOverlay'),
    caption: need<HTMLElement>('roomCaption'),
    figures: need<HTMLElement>('roomFigures'),
    stageEmpty: need<HTMLElement>('stageEmpty'),
    roomLegend: need<HTMLElement>('roomLegend'),
    mapLegend: need<HTMLElement>('mapLegend'),
    cloudToggle: need<HTMLInputElement>('cloudToggle'),
    cloudToggleRow: need<HTMLElement>('cloudToggleRow'),
    cloudToggleLabel: need<HTMLElement>('cloudToggleLabel'),
    roomNote: need<HTMLElement>('roomNote'),
    mapDemo: need<HTMLButtonElement>('mapDemo'),
    scanResults: need<HTMLElement>('scanResults'),
    planeTable: need<HTMLTableElement>('planeTable'),
    objectTable: need<HTMLTableElement>('objectTable'),
    scanTable: need<HTMLTableElement>('scanTable'),
    downloadMap: need<HTMLButtonElement>('downloadMap'),
  };

  let mapView: MapViewHandle | null = null;
  let latest: {
    reconstruction: Reconstruction;
    looks: number;
    sweptRad: number;
    paceRmsDeg: number;
    stoppedEarly: boolean;
  } | null = null;
  let running: AbortController | null = null;

  /** Chirp length and rest, mirroring the defaults `captureScan` fires with. */
  const CHIRP_SEC = 0.25;
  const REST_SEC = 0.13;
  const COUNTDOWN_SEC = 3;

  function timelineFor(looks: number): ReturnType<typeof scanTimeline> {
    return scanTimeline({
      looks,
      chirpSec: CHIRP_SEC,
      restSec: REST_SEC,
      countdownSec: COUNTDOWN_SEC,
    });
  }

  function chosenLooks(): number {
    return Number(ui.scanLength.value) || 56;
  }

  /**
   * Put the turn time on the option that decides it.
   *
   * The instruction used to say "about twenty seconds" whatever was selected,
   * which was right for one of the four lengths. A user who turns in twenty
   * and then stands still spends the rest of the scan firing from one bearing:
   * no new aperture, and a run of looks whose true radius is whatever the
   * stationary arm was, fed straight into the autofocus that fits it.
   */
  function labelScanLengths(): void {
    for (const option of Array.from(ui.scanLength.options)) {
      const looks = Number(option.value);
      if (!Number.isFinite(looks) || looks <= 0) continue;
      const name = option.textContent?.split('—')[0].trim() ?? '';
      option.textContent = `${name} — ${looks} chirps · ${describeDuration(timelineFor(looks).turnSec)}`;
    }
  }

  function describeGesture(): void {
    const turn = describeDuration(timelineFor(chosenLooks()).turnSec);
    ui.scanNote.textContent =
      `Arm out, screen towards you. Turn all the way round in ${turn}, nodding the phone ` +
      'up and down as you go. Stay quiet — a beat will pace you.';
  }

  labelScanLengths();
  describeGesture();
  ui.scanLength.addEventListener('change', describeGesture);

  function cueSettings(): { cueVolume: number; haptics: boolean } {
    switch (ui.cueMode.value) {
      case 'sound':
        return { cueVolume: 1, haptics: false };
      case 'haptic':
        return { cueVolume: 0, haptics: true };
      case 'off':
        return { cueVolume: 0, haptics: false };
      default:
        return { cueVolume: 1, haptics: true };
    }
  }

  function runWorker(
    request: WorkerRequest,
    transfer: Transferable[],
  ): Promise<{ reconstruction: Reconstruction; warnings: string[]; lookCount: number }> {
    return new Promise((resolve, reject) => {
      const worker = new ReconstructWorker();
      worker.onmessage = (event: MessageEvent<WorkerResponse>): void => {
        const message = event.data;
        if (message.type === 'progress') {
          context.setStatus(`${message.stage}…`, 'busy');
          ui.bar.style.width = `${(message.fraction * 100).toFixed(0)}%`;
          return;
        }
        worker.terminate();
        if (message.type === 'error') reject(new Error(message.message));
        else resolve(message);
      };
      worker.onerror = (event): void => {
        worker.terminate();
        reject(new Error(event.message || 'The reconstruction worker failed.'));
      };
      worker.postMessage(request, transfer);
    });
  }

  async function showMap(
    reconstruction: Reconstruction,
    looks: number,
    sweptRad: number,
    extra: { paceRmsDeg?: number; stoppedEarly?: boolean } = {},
  ): Promise<void> {
    latest = {
      reconstruction,
      looks,
      sweptRad,
      paceRmsDeg: extra.paceRmsDeg ?? Number.NaN,
      stoppedEarly: extra.stoppedEarly ?? false,
    };
    ui.stageEmpty.hidden = true;
    ui.roomLegend.hidden = true;
    ui.mapLegend.hidden = false;
    ui.cloudToggleRow.hidden = false;
    ui.roomNote.textContent =
      'Solid where a wall was measured, dashed where none stood clear of the reverberation.';

    describe(reconstruction);
    fillTables(reconstruction, looks, sweptRad);
    context.claimDataSection();
    ui.scanResults.hidden = false;

    if (!context.hasGpu()) return;
    if (!mapView) {
      context.releaseStage();
      try {
        mapView = await createMapView(ui.canvas, ui.overlay);
      } catch {
        return;
      }
    }
    mapView.update(reconstruction);
    mapView.setCloudVisible(ui.cloudToggle.checked);
    updateCloudLabel();
  }

  function updateCloudLabel(): void {
    const counts = mapView?.cloudCounts();
    if (!counts || counts.total === 0) {
      ui.cloudToggleLabel.textContent = 'Show the measured volume behind the surfaces';
      return;
    }
    // The thinning is stated rather than applied quietly: a cloud drawn at one
    // tick in four still looks like a cloud, and nothing on screen would say so.
    ui.cloudToggleLabel.textContent =
      counts.shown < counts.total
        ? `Measured volume — ${counts.shown.toLocaleString()} of ${counts.total.toLocaleString()} cells drawn`
        : `Measured volume — ${counts.total.toLocaleString()} cells`;
  }

  /**
   * The figures under the drawing.
   *
   * Four numbers, each with the thing that qualifies it underneath. This
   * replaced a paragraph that ran every surface, every object and every error
   * bar together into one sentence - which contained the same facts and could
   * not be read, because a caption is not a table and a room with six surfaces
   * in it needs a table.
   */
  function describe(reconstruction: Reconstruction): void {
    const plan = reconstruction.floorPlan;
    const walls = reconstruction.planes.filter((p) => p.kind === 'wall');

    const figures: { label: string; value: string; unit: string; note: string }[] = [
      {
        label: 'Floor area',
        value: Number.isFinite(plan.areaM2) ? plan.areaM2.toFixed(1) : '—',
        unit: 'm²',
        note: `outline closed over ${(plan.closedFraction * 100).toFixed(0)}% of the compass`,
      },
      {
        label: 'Height',
        value: Number.isFinite(plan.heightM) ? plan.heightM.toFixed(2) : '—',
        unit: 'm',
        note: !Number.isFinite(plan.heightM)
          ? 'neither floor nor ceiling stood clear'
          : plan.heightAssumed
            ? `${Number.isFinite(plan.ceilingM) ? 'ceiling' : 'floor'} measured, the other taken as 1.35 m`
            : 'floor and ceiling both measured',
      },
      {
        label: 'Volume',
        value: Number.isFinite(plan.volumeM3) ? plan.volumeM3.toFixed(1) : '—',
        unit: 'm³',
        note: 'floor area times height',
      },
      {
        label: 'Surfaces',
        value: String(walls.length),
        unit: walls.length === 1 ? 'wall' : 'walls',
        note: `${reconstruction.objects.length} object${reconstruction.objects.length === 1 ? '' : 's'} standing in the room`,
      },
    ];

    ui.figures.replaceChildren(
      ...figures.map((figure) => {
        const wrapper = document.createElement('div');
        wrapper.className = 'figure';
        const dt = document.createElement('dt');
        dt.textContent = figure.label;
        const dd = document.createElement('dd');
        const value = document.createElement('span');
        value.className = 'figure-value';
        value.textContent = figure.value;
        const unit = document.createElement('span');
        unit.className = 'figure-unit';
        unit.textContent = figure.unit;
        dd.append(value, unit);
        const note = document.createElement('p');
        note.className = 'figure-note';
        note.textContent = figure.note;
        wrapper.append(dt, dd, note);
        return wrapper;
      }),
    );
    ui.figures.hidden = false;
    ui.caption.textContent = '';
  }

  function fillTables(reconstruction: Reconstruction, looks: number, sweptRad: number): void {
    const { planes, objects, quality, geometry, floorPlan } = reconstruction;

    ui.planeTable.replaceChildren();
    if (planes.length === 0) {
      emptyRow(ui.planeTable, 5, 'No surface stood clear of the reverberation.');
    } else {
      head(ui.planeTable, ['Surface', 'Distance', 'Bearing', 'Looks', 'Spread']);
      const body = ui.planeTable.createTBody();
      for (const plane of planes) {
        const row = body.insertRow();
        if (plane.consensus < 0.6) row.className = 'weak';
        cell(row, plane.kind);
        cell(row, `${plane.offsetM.toFixed(2)} m`);
        cell(row, plane.kind === 'wall' ? `${bearingOf(plane.normal).toFixed(0)}°` : '—');
        cell(row, `${plane.looks} · ${(plane.consensus * 100).toFixed(0)}%`);
        cell(row, Number.isFinite(plane.residualM) ? `±${(plane.residualM * 100).toFixed(0)} cm` : '—');
      }
    }

    ui.objectTable.replaceChildren();
    if (objects.length === 0) {
      emptyRow(
        ui.objectTable,
        5,
        'Nothing stood clear of the reverberation once the walls were accounted for.',
      );
    } else {
      head(ui.objectTable, ['Footprint', 'Top', 'Range', 'Bearing', 'Consistent with']);
      const body = ui.objectTable.createTBody();
      for (const object of objects) {
        const row = body.insertRow();
        if (object.labelConfidence < 0.4) row.className = 'weak';
        cell(row, `${(object.half[0] * 2).toFixed(2)} × ${(object.half[2] * 2).toFixed(2)} m`);
        cell(
          row,
          Number.isFinite(object.topAboveFloorM) ? `${object.topAboveFloorM.toFixed(2)} m` : '—',
        );
        cell(row, `${object.rangeM.toFixed(2)} m`);
        cell(row, `${object.bearingDeg.toFixed(0)}°`);
        cell(row, object.label);
      }
    }

    ui.scanTable.replaceChildren();
    const body = ui.scanTable.createTBody();
    const rows: [string, string][] = [
      [
        'Chirps',
        latest?.stoppedEarly ? `${looks} — stopped early` : String(looks),
      ],
      ['Turned through', `${((sweptRad * 180) / Math.PI).toFixed(0)}°`],
      ['Compass covered', `${(quality.angularCoverage * 100).toFixed(0)}%`],
      ['Pitch swept', `${quality.pitchSpreadDeg.toFixed(0)}°`],
      // The aperture is not asked for, it is measured - and it is the number
      // everything else is scaled by, so it belongs on screen.
      ['Aperture fitted', `${(geometry.armRadiusM * 2).toFixed(2)} m across`],
      ['Outline closed', `${(floorPlan.closedFraction * 100).toFixed(0)}%`],
    ];
    // How nearly the turn matched the beat it was given. This is the input
    // assumption behind every position in the map - a constant rate about a
    // fixed axis - reported as a measured quantity rather than trusted.
    if (latest && Number.isFinite(latest.paceRmsDeg)) {
      rows.push(['Off the beat by', `±${latest.paceRmsDeg.toFixed(0)}° rms`]);
    }
    const counts = mapView?.cloudCounts();
    if (counts && counts.total > 0) {
      rows.push(['Cells over the reverberation', counts.total.toLocaleString()]);
    }
    for (const [label, value] of rows) {
      const row = body.insertRow();
      cell(row, label);
      cell(row, value);
    }
  }

  function lock(busy: boolean, label: string, stoppable = false): void {
    context.setBusy(busy);
    ui.scan.disabled = busy;
    ui.measure.disabled = busy;
    ui.demo.disabled = busy;
    ui.mapDemo.disabled = busy;
    ui.scan.classList.toggle('is-running', busy);
    ui.scan.textContent = label;
    ui.progress.hidden = !busy;
    ui.stop.hidden = !stoppable;
    ui.stop.disabled = !stoppable;
    if (stoppable) ui.stop.textContent = 'Stop';
    if (!busy) {
      ui.ready.hidden = true;
      ui.pace.textContent = '';
      ui.pace.dataset.verdict = 'on';
    }
    if (busy) ui.bar.style.width = '0%';
  }

  const PACE_TEXT = {
    on: 'On the beat',
    behind: 'Behind — turn faster',
    ahead: 'Ahead — ease off',
  } as const;

  /**
   * The live readout, and the only place the four numbers the scan is graded
   * on are visible while there is still scan left to fix them with.
   *
   * Every one of these used to arrive as a warning after the turn was over,
   * where the only available remedy was to do the whole thing again.
   */
  function showProgress(progress: ScanProgress): void {
    ui.bar.style.width = `${((progress.done / progress.total) * 100).toFixed(0)}%`;
    ui.turn.textContent = `${progress.done} of ${progress.total} chirps`;

    const tilting = progress.pitchSpreadDeg < TILT_TARGET_DEG && progress.done > progress.total * 0.3;
    if (tilting && progress.verdict === 'on') {
      ui.pace.textContent = 'Nod the phone up and down';
      ui.pace.dataset.verdict = 'tilt';
    } else {
      ui.pace.textContent = PACE_TEXT[progress.verdict];
      ui.pace.dataset.verdict = progress.verdict;
    }

    ui.meterTurned.textContent = `${((progress.sweptRad * 180) / Math.PI).toFixed(0)}°`;
    ui.meterCoverage.textContent = `${(progress.coverage * 100).toFixed(0)}%`;
    ui.meterPitch.textContent = `${progress.pitchSpreadDeg.toFixed(0)}°`;
    ui.meterLeft.textContent = `${progress.remainingSec.toFixed(0)} s`;
    ui.meterPitch.classList.toggle('is-short', progress.pitchSpreadDeg < TILT_TARGET_DEG);
    ui.meterCoverage.classList.toggle(
      'is-short',
      progress.coverage < 0.6 && progress.done > progress.total * 0.5,
    );
  }

  async function runScan(): Promise<void> {
    if (context.isBusy()) return;
    const blocker = captureBlocker();
    if (blocker) {
      context.setStatus(blocker, 'error');
      return;
    }

    lock(true, 'Scanning…', true);
    context.setWarnings([]);
    const controller = new AbortController();
    running = controller;
    try {
      // Requested from inside the click that started the scan: iOS rejects
      // outright otherwise, and a scan that does not know which way the phone
      // was pointing is not a scan.
      const granted = await requestOrientationPermission();
      const looks = chosenLooks();

      const capture = await captureScan({
        looks,
        countdownSec: COUNTDOWN_SEC,
        ...cueSettings(),
        signal: controller.signal,
        onStatus: (message) => context.setStatus(message, 'busy'),
        onCountdown: (secondsLeft) => {
          ui.ready.hidden = secondsLeft <= 0;
          ui.progress.hidden = secondsLeft > 0;
          if (secondsLeft > 0) {
            // Replaced rather than rewritten, so the count animation restarts.
            const count = document.createElement('p');
            count.className = 'scan-count';
            count.id = 'scanCount';
            count.textContent = String(secondsLeft);
            ui.count.replaceWith(count);
            ui.count = count;
          }
        },
        onProgress: showProgress,
      });

      if (!granted) {
        capture.warnings.push(
          'Orientation permission was refused, so the scan assumed a steady turn. Surfaces are placed far less accurately.',
        );
      }

      // The recording is in hand from here on, so there is nothing left to
      // stop - and a Stop button that cancels nothing is worse than none.
      lock(true, 'Reconstructing…', false);
      context.setStatus('Reconstructing…', 'busy');
      const recordings = capture.looks.map((l) => new Float32Array(l.recording));
      const result = await runWorker(
        {
          type: 'scan',
          looks: capture.looks.map((l, i) => ({
            recording: recordings[i],
            rotation: l.rotation,
            timeSec: l.timeSec,
          })),
          preset: 'scan',
          sampleRate: capture.sampleRate,
          temperatureC: context.temperatureC(),
        },
        recordings.map((r) => r.buffer as Transferable),
      );

      await showMap(result.reconstruction, result.lookCount, capture.sweptRad, {
        paceRmsDeg: capture.paceRmsDeg,
        stoppedEarly: capture.stoppedEarly,
      });
      context.setWarnings([
        ...capture.warnings,
        ...result.warnings,
        ...result.reconstruction.quality.warnings,
      ]);
      const walls = result.reconstruction.planes.filter((p) => p.kind === 'wall').length;
      const objects = result.reconstruction.objects.length;
      const opening = capture.stoppedEarly
        ? `Stopped at ${result.lookCount} of ${capture.plannedLooks} chirps`
        : `${result.lookCount} chirps`;
      context.setStatus(
        `${opening} · ${walls} wall${walls === 1 ? '' : 's'} · ${objects} object${objects === 1 ? '' : 's'}.`,
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      const stopped = error instanceof DOMException && error.name === 'AbortError';
      context.setStatus(
        stopped
          ? 'Scan stopped before anything was recorded.'
          : message.includes('Permission') || message.includes('NotAllowed')
            ? 'Microphone permission denied. Chirpr cannot map without it.'
            : `Scan failed: ${message}`,
        stopped ? 'idle' : 'error',
      );
    } finally {
      running = null;
      lock(false, 'Map the room');
    }
  }

  async function runMapDemo(): Promise<void> {
    if (context.isBusy()) return;
    lock(true, 'Simulating…');
    context.setStatus('Simulating a scan of a room with a known floor plan…', 'busy');
    try {
      const chosen = context.demoScene();
      const scene = (SCENES as string[]).includes(chosen) ? (chosen as SceneName) : 'bedroom';
      const result = await runWorker(
        { type: 'demo', scene, looks: 64, armRadiusM: 0.6, temperatureC: context.temperatureC() },
        [],
      );
      await showMap(result.reconstruction, result.lookCount, 2 * Math.PI);
      context.setWarnings(result.reconstruction.quality.warnings);
      const walls = result.reconstruction.planes.filter((p) => p.kind === 'wall').length;
      context.setStatus(
        `Simulated ${scene} · ${result.lookCount} chirps · ${walls} wall${walls === 1 ? '' : 's'}.`,
      );
    } catch (error) {
      context.setStatus(
        `Demo failed: ${error instanceof Error ? error.message : String(error)}`,
        'error',
      );
    } finally {
      lock(false, 'Map the room');
    }
  }

  ui.scan.addEventListener('click', () => void runScan());
  ui.stop.addEventListener('click', () => {
    if (!running) return;
    ui.stop.disabled = true;
    ui.stop.textContent = 'Stopping…';
    running.abort();
  });
  ui.mapDemo.addEventListener('click', () => void runMapDemo());
  ui.cloudToggle.addEventListener('change', () => {
    mapView?.setCloudVisible(ui.cloudToggle.checked);
    updateCloudLabel();
  });

  ui.downloadMap.addEventListener('click', () => {
    if (!latest) return;
    const { reconstruction, looks, sweptRad } = latest;
    const summary = {
      scan: {
        looks,
        sweptDeg: (sweptRad * 180) / Math.PI,
        angularCoverage: reconstruction.quality.angularCoverage,
        pitchSpreadDeg: reconstruction.quality.pitchSpreadDeg,
        armRadiusM: reconstruction.geometry.armRadiusM,
        boresight: reconstruction.geometry.boresight,
        focusScore: reconstruction.quality.focusScore,
      },
      floorPlan: {
        areaM2: reconstruction.floorPlan.areaM2,
        heightM: reconstruction.floorPlan.heightM,
        heightAssumed: reconstruction.floorPlan.heightAssumed,
        volumeM3: reconstruction.floorPlan.volumeM3,
        closedFraction: reconstruction.floorPlan.closedFraction,
        // The polygon itself, so the plan can be drawn somewhere else.
        outline: reconstruction.floorPlan.outline,
        measured: reconstruction.floorPlan.measured,
      },
      surfaces: reconstruction.planes.map((plane) => ({
        kind: plane.kind,
        normal: plane.normal,
        offsetM: plane.offsetM,
        bearingDeg: plane.kind === 'wall' ? bearingOf(plane.normal) : null,
        looks: plane.looks,
        consensus: plane.consensus,
        residualM: plane.residualM,
        score: plane.score,
      })),
      objects: reconstruction.objects.map((object) => ({
        centre: object.centre,
        half: object.half,
        yawRad: object.yawRad,
        topAboveFloorM: object.topAboveFloorM,
        rangeM: object.rangeM,
        bearingDeg: object.bearingDeg,
        support: object.support,
        voxels: object.voxels,
        peakDb: object.peakDb,
        label: object.label,
        labelConfidence: object.labelConfidence,
      })),
      warnings: reconstruction.quality.warnings,
    };
    const now = new Date();
    const pad = (v: number): string => String(v).padStart(2, '0');
    const name = `chirpr-map-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(
      now.getHours(),
    )}${pad(now.getMinutes())}${pad(now.getSeconds())}.json`;
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(summary, null, 2)], { type: 'application/json' }),
    );
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  });
}
